import type { AuditCenterRow, AuditCenterSource, AuditSortKey } from '@/types/audit'
import type { LabelUsage } from '@/api/productLabelsApi'
import { patientKindLabel } from '@/lib/auditCenter'
import { PERMISSIONS } from '@/lib/permissions'
import { USAGE_LABEL } from '@/lib/productLabels'
import {
  changesOf,
  humanizeKey,
  pageName,
  readableValue,
  subjectLabel,
  type ChangeTone,
  type FieldChange,
} from '@/lib/auditPresentation'
import { recordTarget, subjectTarget, type AuditLinkTarget } from '@/lib/auditLinks'
import {
  linkSource,
  productSource,
  recordSource,
  storageSource,
  subjectSource,
  submissionSource,
  type TrailSource,
} from '@/lib/auditTrailSources'

/**
 * One layout per audit trail (the client, 2026-09-29: "each one will have a
 * different user interface … we don't have a generic audit trail").
 *
 * Every trail answers different questions, so each has its own columns and
 * its own words: Storage says what stock moved from where to where, Inventory
 * what was received, consumed and returned and how much, Equipment what was
 * booked, started and finished and how long it took, Procedures what was done
 * on which form and step and what it used, User who signed in, for how long and
 * where they went. Pure, so each layout's rules are unit-tested.
 */

/**
 * Where a cell's value leads, as every other audit trail's references do: the
 * item's own trail in the audit pop-up (`source`), else the item's page
 * (`target`) for an item with no trail of its own.
 */
export interface CellLink {
  source?: TrailSource | null
  target?: AuditLinkTarget | null
}

/** What one cell shows. The component draws each kind; the layouts choose them. */
export type AuditCell =
  | { kind: 'datetime' }
  | { kind: 'actor' }
  | { kind: 'record' }
  | { kind: 'text'; value: string | null; sub?: string | null; strong?: boolean; link?: CellLink }
  | { kind: 'badge'; label: string; tone: ChangeTone }
  | { kind: 'lines'; lines: { label?: string; value: string }[] }
  | { kind: 'changes'; changes: FieldChange[] }

export interface AuditColumn {
  /** Stable: it keys the person's saved column layout. */
  id: string
  header: string
  /** Starting width in px; the person can resize it, as on every other table. */
  defaultWidth: number
  /** Hidden until switched on in the Columns menu. */
  defaultHidden?: boolean
  /**
   * What the server orders by when this column's header is clicked. Only a
   * column that shows one value has one; the server sorts everything paging
   * reaches, so the order is never just the page on screen.
   */
  sortKey?: AuditSortKey
  cell: (row: AuditCenterRow) => AuditCell
}

export interface AuditView {
  /** The saved-layout key for this trail's table — a released id, see tableColumnPrefs. */
  tableId: string
  title: string
  columns: AuditColumn[]
}

// ─── Shared helpers ──────────────────────────────────────────────────────────

const fact = (row: AuditCenterRow, key: string): string | null => {
  const v = row.facts?.[key]
  return v === null || v === undefined || v === '' ? null : String(v)
}
const text = (value: string | null | undefined, sub?: string | null, strong?: boolean, link?: CellLink): AuditCell => ({
  kind: 'text',
  value: value ?? null,
  sub: sub ?? null,
  strong,
  ...(link && (link.source || link.target) ? { link } : {}),
})
const obj = (v: unknown): Record<string, unknown> | null =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null
const at = (v: unknown): string | null => (v === null || v === undefined || v === '' ? null : readableValue(v))
const titleCase = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1)

const DATE: AuditColumn = { id: 'when', header: 'Date & Time', defaultWidth: 140, sortKey: 'when', cell: () => ({ kind: 'datetime' }) }
const BY: AuditColumn = { id: 'by', header: 'Performed By', defaultWidth: 230, sortKey: 'by', cell: () => ({ kind: 'actor' }) }
const REASON: AuditColumn = {
  id: 'reason',
  header: 'Reason',
  defaultWidth: 200,
  sortKey: 'reason',
  cell: (row) => text(row.reason?.trim() || null),
}

/**
 * The patient, kit or work order a row belongs to, labelled for what it is. A
 * patient opens their trail; a kit run or MES order, which has none, its page.
 */
const subjectCell = (row: AuditCenterRow): AuditCell => {
  if (!row.subjectKey) return text(null)
  const keyed = asKeyed(row)
  // A deleted run is still named, but its page would find nothing.
  const target = row.procedureDeleted && keyed.subjectKeyType !== 'mrn' ? null : subjectTarget(keyed)
  return text(row.subjectKey, subjectLabel(row.subjectKey, row.subjectKeyType), undefined, { source: subjectSource(keyed), target })
}

/**
 * The row with its subject typed as its label reads it (subjectLabel): a kit
 * form keeps its KIT ID where an MRN goes, and that is still a kit, never a
 * patient to open the trail of.
 */
function asKeyed(row: AuditCenterRow): AuditCenterRow {
  const key = row.subjectKey ?? ''
  const type = /^KIT-/i.test(key) ? 'kit' : /^WO-/i.test(key) ? 'wo' : /^MFG-/i.test(key) ? 'mfg' : row.subjectKeyType
  return type === row.subjectKeyType ? row : { ...row, subjectKeyType: type }
}

/** What a Form column can name: a form, a patient's record, a run, a template. */
const FORM_RECORDS = new Set(['submission', 'patient', 'procedure', 'form_definition', 'procedure_template'])

/** The form a row was on: its submission's trail, else the record it belongs to — as a trail or a page. */
function formLink(row: AuditCenterRow): CellLink {
  const record = row.record && FORM_RECORDS.has(row.record.kind) ? row.record : null
  return { source: submissionSource(row) ?? recordSource(record), target: recordTarget(record, asKeyed(row)) }
}

/** The lot or unit a row used: named in its links, else the record the row belongs to. */
function relatedSource(row: AuditCenterRow, kind: 'lot' | 'equipment'): TrailSource | null {
  const entityType = kind === 'lot' ? 'inventory_lot' : 'equipment'
  const link = row.links?.find((l) => l.entityType === entityType)
  if (link) return linkSource(link)
  return row.record?.kind === kind ? recordSource(row.record) : null
}

/** The step and form a row was for, opening that form's trail. */
const forCell = (row: AuditCenterRow): AuditCell =>
  text(stepAndForm(row), row.subjectKey ? `${subjectLabel(row.subjectKey, row.subjectKeyType)} ${row.subjectKey}` : null, undefined, {
    source: submissionSource(row),
  })

/** "Kitting · KIT FORM" — the step and the form it is on. */
const stepAndForm = (row: AuditCenterRow): string | null =>
  [fact(row, 'step'), fact(row, 'form')].filter(Boolean).join(' · ') || null

const DEFINITION_KINDS = new Set(['form_definition', 'procedure_template', 'library_field'])

/** A form template, procedure template or library field: created, a new version, (in)activated, a form updated. */
function definitionEvent(row: AuditCenterRow): { label: string; tone: ChangeTone } | null {
  if (!row.record || !DEFINITION_KINDS.has(row.record.kind)) return null
  const labels: Record<string, { label: string; tone: ChangeTone }> = {
    created: { label: 'Created', tone: 'create' },
    version_created: { label: 'New version', tone: 'update' },
    updated_in_place: { label: 'Updated', tone: 'update' },
    activated: { label: 'Activated', tone: 'complete' },
    inactivated: { label: 'Inactivated', tone: 'remove' },
    cascade_inactivated: { label: 'Inactivated', tone: 'remove' },
    form_ref_updated: { label: 'Form updated', tone: 'update' },
    deleted: { label: 'Deleted', tone: 'remove' },
  }
  return labels[row.eventType] ?? { label: humanizeKey(row.eventType), tone: 'update' }
}

/** What a new version changed: the fields (a form) or steps (a template) removed, added and renamed. */
function versionLines(row: AuditCenterRow): { label: string; value: string }[] {
  return [
    fact(row, 'removed') ? { label: 'Removed', value: fact(row, 'removed')! } : null,
    fact(row, 'added') ? { label: 'Added', value: fact(row, 'added')! } : null,
    fact(row, 'renamed') ? { label: 'Renamed', value: fact(row, 'renamed')! } : null,
  ].filter((l): l is { label: string; value: string } => !!l)
}

// ─── Storage: what stock moved, from where to where ─────────────────────────

const STOCK_MOVES = /^(put away|taken out|moved)/i

export function storageActivity(row: AuditCenterRow): { label: string; tone: ChangeTone } {
  const label = row.action ?? humanizeKey(row.eventType)
  if (/deleted/i.test(label)) return { label, tone: 'remove' }
  if (STOCK_MOVES.test(label)) return { label, tone: 'stock' }
  if (/created|built|seeded/i.test(label)) return { label, tone: 'create' }
  return { label, tone: 'update' }
}

export const STORAGE_VIEW: AuditView = {
  tableId: 'audit-trail-storage-v1',
  title: 'Storage Movement Log',
  columns: [
    DATE,
    { id: 'location', header: 'Location', defaultWidth: 200, sortKey: 'record', cell: () => ({ kind: 'record' }) },
    { id: 'activity', header: 'Activity', defaultWidth: 140, sortKey: 'action', cell: (row) => ({ kind: 'badge', ...storageActivity(row) }) },
    {
      id: 'code',
      header: 'Material Code',
      defaultWidth: 140,
      sortKey: 'materialCode',
      cell: (row) => text(fact(row, 'materialCode') ?? (fact(row, 'din') ? `DIN ${fact(row, 'din')}` : null)),
    },
    {
      id: 'material',
      header: 'Material Description',
      defaultWidth: 200,
      sortKey: 'material',
      cell: (row) => text(fact(row, 'materialName') ?? fact(row, 'productCode')),
    },
    {
      id: 'lot',
      header: 'Lot / DIN',
      defaultWidth: 140,
      sortKey: 'lot',
      // A lot opens its storage trail; a product's DIN, the product's trail.
      cell: (row) =>
        fact(row, 'lotNumber')
          ? text(fact(row, 'lotNumber'), null, undefined, { source: relatedSource(row, 'lot') })
          : text(fact(row, 'din'), null, undefined, { source: productSource(row) }),
    },
    {
      id: 'previous',
      header: 'Previous Location',
      defaultWidth: 200,
      sortKey: 'from',
      // A put-away's earlier position, or where a relocated position used to be.
      cell: (row) => text(row.fromLocation && row.fromLocation !== row.record?.label ? row.fromLocation : null),
    },
    BY,
    {
      id: 'details',
      header: 'Details',
      defaultWidth: 200,
      cell: (row) => {
        // What the position, zone or transfer was given, or what an edit changed, in words.
        if (row.detailLines?.length) return { kind: 'changes', changes: row.detailLines }
        const pairs = (row.detail ?? '').split('; ').map((p) => /^(.+?): (.*) → (.*)$/.exec(p))
        if (row.detail && pairs.every(Boolean)) {
          return { kind: 'changes', changes: pairs.map((m) => ({ label: humanizeKey(m![1]), from: m![2], to: m![3] })) }
        }
        return text(row.detail ?? null)
      },
    },
    REASON,
  ],
}

// ─── Inventory: received, consumed, returned — and how much ─────────────────

export function inventoryTransaction(row: AuditCenterRow): { label: string; tone: ChangeTone } {
  const label = row.action ?? humanizeKey(row.eventType)
  if (/^received/i.test(label)) return { label, tone: 'create' }
  if (/^(consumed|reserved|released$|put away|taken out)/i.test(label)) return { label, tone: 'stock' }
  if (/quarantine/i.test(label)) return { label, tone: 'remove' }
  if (/available|in process/i.test(label)) return { label, tone: 'complete' }
  return { label, tone: 'update' }
}

export const INVENTORY_VIEW: AuditView = {
  tableId: 'audit-trail-inventory-v1',
  title: 'Inventory Transaction Log',
  columns: [
    DATE,
    { id: 'code', header: 'Material Code', defaultWidth: 140, sortKey: 'materialCode', cell: (row) => text(fact(row, 'materialCode')) },
    { id: 'material', header: 'Material Description', defaultWidth: 200, sortKey: 'material', cell: (row) => text(fact(row, 'materialName')) },
    {
      id: 'lot',
      header: 'Lot Number',
      defaultWidth: 140,
      sortKey: 'lot',
      cell: (row) => text(fact(row, 'lotNumber'), null, undefined, { source: relatedSource(row, 'lot') }),
    },
    { id: 'transaction', header: 'Transaction', defaultWidth: 140, sortKey: 'action', cell: (row) => ({ kind: 'badge', ...inventoryTransaction(row) }) },
    {
      id: 'qty',
      header: 'Quantity',
      defaultWidth: 90,
      sortKey: 'quantity',
      cell: (row) => text(fact(row, 'quantity') ?? (row.quantity != null ? String(row.quantity) : null), null, true),
    },
    {
      id: 'status',
      header: 'Status / Location',
      defaultWidth: 200,
      cell: (row) => {
        const from = fact(row, 'statusFrom')
        const to = fact(row, 'statusTo')
        if (from || to) return { kind: 'changes', changes: [{ label: 'Status', from: titleCase(from ?? 'none'), to: titleCase(to ?? 'none') }] }
        // Where the lot went: from one position, to another, or both.
        const lines = [
          row.fromLocation ? { label: 'From', value: row.fromLocation } : null,
          row.toLocation ? { label: 'To', value: row.toLocation } : null,
        ].filter((l): l is { label: string; value: string } => l !== null)
        return lines.length ? { kind: 'lines', lines } : text(null)
      },
    },
    // The procedure step that reserved or consumed the stock.
    { id: 'for', header: 'Used For', defaultWidth: 200, cell: forCell },
    BY,
    REASON,
  ],
}

// ─── Product: created, stored, split — and what changed ─────────────────────

export function productActivity(row: AuditCenterRow): { label: string; tone: ChangeTone } {
  const e = row.eventType.replace(/^product_/, '')
  const label = humanizeKey(e)
  if (/created|received/.test(e)) return { label, tone: 'create' }
  if (/split|pool|stored|moved/.test(e)) return { label, tone: 'stock' }
  if (/deleted|discard/.test(e)) return { label, tone: 'remove' }
  return { label, tone: 'update' }
}

export const PRODUCT_VIEW: AuditView = {
  tableId: 'audit-trail-product-v1',
  title: 'Product History',
  columns: [
    DATE,
    // The product, the patient and the storage position each open their own
    // trail in the pop-up, as a storage row's Location does.
    {
      id: 'din',
      header: 'DIN',
      defaultWidth: 140,
      sortKey: 'din',
      cell: (row) => text(row.din ?? row.record?.label ?? null, null, true, { source: productSource(row) }),
    },
    {
      id: 'code',
      header: 'Product Code',
      defaultWidth: 120,
      sortKey: 'productCode',
      cell: (row) => text(row.productCode ?? null, null, undefined, { source: productSource(row) }),
    },
    { id: 'activity', header: 'Activity', defaultWidth: 140, cell: (row) => ({ kind: 'badge', ...productActivity(row) }) },
    { id: 'patient', header: 'Patient', defaultWidth: 140, sortKey: 'subject', cell: subjectCell },
    {
      id: 'change',
      header: 'Change',
      defaultWidth: 200,
      // The storage position has a column of its own.
      cell: (row) => ({ kind: 'changes', changes: changesOf(row).filter((c) => !/^(Path|Storage node)$/.test(c.label)) }),
    },
    {
      id: 'storage',
      header: 'Storage Location',
      defaultWidth: 200,
      sortKey: 'storage',
      cell: (row) => text(row.storageNodeLabel ?? null, null, undefined, { source: storageSource(row) }),
    },
    BY,
    { id: 'description', header: 'Description', defaultWidth: 220, sortKey: 'description', cell: (row) => text(row.description ?? null) },
    REASON,
  ],
}

// ─── Equipment: booked, started, finished — and how long it took ────────────

const EQUIPMENT_ACTIVITY: Record<string, { label: string; tone: ChangeTone }> = {
  created: { label: 'Scheduled', tone: 'create' },
  assigned: { label: 'Assigned', tone: 'update' },
  started: { label: 'Started', tone: 'stock' },
  completed: { label: 'Completed', tone: 'complete' },
  rescheduled: { label: 'Rescheduled', tone: 'update' },
  edited: { label: 'Edited', tone: 'update' },
  updated: { label: 'Updated', tone: 'update' },
  deleted: { label: 'Deleted', tone: 'remove' },
  signature_applied: { label: 'Signed', tone: 'complete' },
  deviation_recorded: { label: 'Deviation', tone: 'remove' },
  undo_end: { label: 'Reopened', tone: 'update' },
  reserved: { label: 'Booked', tone: 'stock' },
  released: { label: 'Released', tone: 'stock' },
  scan_verified: { label: 'Scan verified', tone: 'complete' },
  scan_refused: { label: 'Scan refused', tone: 'remove' },
}

export function equipmentActivity(row: AuditCenterRow): { label: string; tone: ChangeTone } {
  return EQUIPMENT_ACTIVITY[row.eventType] ?? { label: humanizeKey(row.eventType), tone: 'update' }
}

/** Due date, when it started and ended, how long it took, or the booked window. */
export function equipmentTiming(row: AuditCenterRow): { label?: string; value: string }[] {
  const before = obj(row.oldValue)
  const after = obj(row.newValue)
  const lines: { label?: string; value: string }[] = []
  const push = (label: string, value: string | null) => {
    if (value) lines.push({ label, value })
  }
  switch (row.eventType) {
    case 'created':
      push('Due', at(after?.dueDate))
      break
    case 'rescheduled':
      push('Due', before?.dueDate || after?.dueDate ? `${at(before?.dueDate) ?? '—'} → ${at(after?.dueDate) ?? '—'}` : null)
      break
    case 'started':
      push('Started', at(after?.actualStart))
      break
    case 'completed': {
      push('Ended', at(after?.actualEnd))
      const hours = after?.actualHours
      if (typeof hours === 'number') push('Duration', `${Math.round(hours * 10) / 10} h`)
      break
    }
    case 'reserved':
      push('From', at(after?.startsAt))
      push('To', at(after?.endsAt))
      break
    case 'released':
      push('Released', at(after?.releasedAt))
      break
  }
  const offline = offlineSync(row)
  if (offline) lines.push({ value: offline })
  return lines
}

/**
 * "Offline · synced Oct 21, 14:32" for a row a phone wrote with no connection
 * (z8nrz7fh5c), else null. The row is dated when the person did it; this says
 * when the server received it.
 */
export function offlineSync(row: AuditCenterRow): string | null {
  const after = obj(row.newValue)
  if (after?.channel !== 'offline') return null
  const synced = at(after.receivedAt)
  return synced ? `Offline · synced ${synced}` : 'Offline'
}

/** What a completion found, a deviation, a signature's meaning, or the fields an edit changed. */
export function equipmentOutcome(row: AuditCenterRow): AuditCell {
  const after = obj(row.newValue)
  switch (row.eventType) {
    case 'completed':
      return text(at(after?.result) ? `Result: ${at(after?.result)}` : null)
    case 'deviation_recorded':
      return text(at(after?.deviation))
    case 'signature_applied':
      return text(at(after?.meaning), at(after?.signedBy))
    case 'edited':
    case 'updated':
      return { kind: 'changes', changes: changesOf(row) }
    case 'scan_refused':
    case 'scan_verified':
      return text(row.description ?? null)
    default:
      return text(null)
  }
}

export const EQUIPMENT_VIEW: AuditView = {
  tableId: 'audit-trail-equipment-v1',
  title: 'Equipment Log',
  columns: [
    DATE,
    { id: 'equipment', header: 'Equipment', defaultWidth: 140, sortKey: 'record', cell: () => ({ kind: 'record' }) },
    { id: 'activity', header: 'Activity', defaultWidth: 130, cell: (row) => ({ kind: 'badge', ...equipmentActivity(row) }) },
    {
      id: 'category',
      header: 'Category',
      defaultWidth: 200,
      sortKey: 'category',
      cell: (row) => text(fact(row, 'category'), fact(row, 'task') ? humanizeKey(fact(row, 'task')!) : null),
    },
    { id: 'timing', header: 'Schedule & Timing', defaultWidth: 220, cell: (row) => ({ kind: 'lines', lines: equipmentTiming(row) }) },
    // The procedure step a booking was made for.
    { id: 'for', header: 'For', defaultWidth: 200, cell: forCell },
    { id: 'outcome', header: 'Result / Changes', defaultWidth: 200, cell: equipmentOutcome },
    BY,
    REASON,
  ],
}

// ─── Procedures: what was done on which form and step, and what it used ─────

/** What was done: a field entered or changed, a step started or signed, stock consumed, a unit booked. */
export function procedureActivity(row: AuditCenterRow): { label: string; tone: ChangeTone } {
  const definition = definitionEvent(row)
  if (definition) return definition
  if (row.source === 'inventory') return { label: row.action ?? humanizeKey(row.eventType), tone: 'stock' }
  if (row.source === 'equipment') return { label: row.eventType === 'released' ? 'Unit released' : 'Unit booked', tone: 'stock' }
  if (row.source === 'form') return fieldChange(row)
  if (row.source === 'process') {
    const verb: Record<string, string> = {
      created: 'Step set up',
      assigned: 'Step assigned',
      started: 'Step started',
      ended: 'Step ended',
      completed: 'Step completed',
      signature_applied: 'Step signed',
      selections_updated: 'Resources selected',
    }
    return { label: verb[row.eventType] ?? humanizeKey(row.eventType), tone: row.eventType === 'completed' || row.eventType === 'signature_applied' ? 'complete' : 'update' }
  }
  return { label: humanizeKey(row.eventType.replace(/^(procedure|product)_/, '')), tone: 'update' }
}

/** A step's selected units and lots, by name: "PSEP-A1 from Sep 11, 12:13", "LOT-26-0002-1 × 1 (MAT-000102)". */
const SELECTIONS: Record<string, { label: string; each: (s: Record<string, unknown>) => string }> = {
  equipmentSelections: {
    label: 'Equipment',
    each: (s) => [String(s.equipmentLabel ?? 'Unit'), at(s.expectedStart) && `from ${at(s.expectedStart)}`].filter(Boolean).join(' '),
  },
  inventorySelections: {
    label: 'Inventory',
    each: (s) => {
      const lots = (Array.isArray(s.lots) ? s.lots : []).map((l) => obj(l)).filter((l): l is Record<string, unknown> => !!l)
      return [lots.map((l) => `${l.lot} × ${l.qty}`).join(', ') || 'No lot', s.itemLabel ? `(${s.itemLabel})` : null].filter(Boolean).join(' ')
    },
  },
}
const selections = (key: string, v: unknown): string => {
  const list = (Array.isArray(v) ? v : []).map((s) => obj(s)).filter((s): s is Record<string, unknown> => !!s)
  return list.length ? list.map(SELECTIONS[key].each).join('; ') : '—'
}

/** A process step's changes, its selected equipment and lots named rather than shown as their ids. */
function stepChanges(row: AuditCenterRow): FieldChange[] {
  const before = obj(row.oldValue)
  const after = obj(row.newValue)
  const rest = (o: Record<string, unknown> | null) => o && Object.fromEntries(Object.entries(o).filter(([k]) => !(k in SELECTIONS)))
  const picked = Object.keys(SELECTIONS)
    .filter((k) => selections(k, before?.[k]) !== selections(k, after?.[k]))
    .map((k) => ({ label: SELECTIONS[k].label, from: before ? selections(k, before[k]) : undefined, to: selections(k, after?.[k]) }))
  return [...picked, ...changesOf({ ...row, oldValue: rest(before), newValue: rest(after) })]
}

/** Old → new, one line per changed value; a step's signature and a stock move read as their new value. */
function procedureValues(row: AuditCenterRow): FieldChange[] {
  const version = versionLines(row)
  if (version.length) return version.map((l) => ({ label: l.label, to: l.value }))
  if (row.source === 'inventory') {
    const qty = fact(row, 'quantity')
    const what = [fact(row, 'materialName'), fact(row, 'materialCode') && `(${fact(row, 'materialCode')})`].filter(Boolean).join(' ')
    return [{ label: 'Quantity', to: `${qty ?? '?'} × ${what || 'stock'}` }]
  }
  if (row.source === 'equipment') {
    const after = obj(row.newValue)
    if (row.eventType === 'reserved') return [{ label: 'Booked', to: `${at(after?.startsAt) ?? '—'} → ${at(after?.endsAt) ?? '—'}` }]
    if (row.eventType === 'released') return [{ label: 'Released', to: at(after?.releasedAt) ?? '—' }]
    return []
  }
  if (row.source === 'process' && row.eventType === 'signature_applied') {
    const after = obj(row.newValue)
    return [{ label: 'Signature', to: [at(after?.meaning), at(after?.signedBy)].filter(Boolean).join(' — ') }]
  }
  return (row.source === 'process' ? stepChanges(row) : changesOf(row)).filter((c) => !/^(Comments|Configured process|Checklist)$/.test(c.label))
}

/**
 * A procedure row's old or new values. One changed value of the field already
 * named under Step / Field needs no label of its own; several are each named.
 */
function valueLines(row: AuditCenterRow, side: 'from' | 'to'): { label?: string; value: string }[] {
  const changes = procedureValues(row)
  const named = fact(row, 'step') ?? fact(row, 'field') ?? row.itemChanged ?? null
  const bare = changes.length === 1 && changes[0].label === named
  return changes
    .filter((c) => (side === 'from' ? c.from !== undefined : true))
    .map((c) => ({ label: bare ? undefined : c.label, value: side === 'from' ? c.from! : c.to }))
}

export const PROCEDURE_VIEW: AuditView = {
  tableId: 'audit-trail-procedure-v1',
  title: 'Procedure Audit Trail',
  columns: [
    DATE,
    { id: 'subject', header: 'Subject', defaultWidth: 140, sortKey: 'subject', cell: subjectCell },
    {
      id: 'form',
      header: 'Form',
      defaultWidth: 200,
      sortKey: 'form',
      cell: (row) =>
        text(
          fact(row, 'form') ?? (row.record?.kind === 'submission' ? row.record.label : row.record?.kind === 'procedure' ? 'Procedure' : null),
          null,
          undefined,
          formLink(row),
        ),
    },
    {
      id: 'step',
      header: 'Step / Field',
      defaultWidth: 200,
      sortKey: 'step',
      cell: (row) => text(fact(row, 'step') ?? fact(row, 'field') ?? row.itemChanged ?? null, null, true),
    },
    { id: 'activity', header: 'Activity', defaultWidth: 140, cell: (row) => ({ kind: 'badge', ...procedureActivity(row) }) },
    {
      id: 'old',
      header: 'Old Value',
      defaultWidth: 200,
      cell: (row) => ({ kind: 'lines', lines: valueLines(row, 'from') }),
    },
    {
      id: 'new',
      header: 'New Value',
      defaultWidth: 200,
      cell: (row) => ({ kind: 'lines', lines: valueLines(row, 'to') }),
    },
    {
      id: 'linked',
      header: 'Linked Item',
      defaultWidth: 200,
      // The inventory lot consumed, or the equipment used — each opening its trail.
      cell: (row) =>
        row.source === 'inventory'
          ? text(fact(row, 'lotNumber') ? `Lot ${fact(row, 'lotNumber')}` : null, fact(row, 'materialCode'), undefined, {
              source: relatedSource(row, 'lot'),
            })
          : row.source === 'equipment'
            ? text(fact(row, 'equipment'), 'Equipment', undefined, { source: relatedSource(row, 'equipment') })
            : row.din
              ? text(`DIN ${row.din}`, row.productCode ?? null, undefined, { source: productSource(row) })
              : text(null),
    },
    BY,
    REASON,
  ],
}

// ─── Field: each form field, entered, changed or cleared ────────────────────

/**
 * What happened to a field: entered for the first time, changed, cleared, a
 * form completed or unlocked, a patient's demographic corrected or updated from
 * Epic, or a field / form definition changed.
 */
export function fieldChange(row: AuditCenterRow): { label: string; tone: ChangeTone } {
  if (row.record?.kind === 'patient') {
    return row.eventType === 'epic_update' ? { label: 'Updated from Epic', tone: 'update' } : { label: 'Corrected', tone: 'update' }
  }
  const definition = definitionEvent(row)
  if (definition) return definition
  if (row.transactionType === 'completion' || row.eventType === 'form_completed') return { label: 'Form completed', tone: 'complete' }
  if (row.eventType === 'form_unlocked') return { label: 'Form unlocked', tone: 'update' }
  // The "Skip Form" action (z8nrz7fn5r); its reason is in the Reason column.
  if (row.eventType === 'form_skipped') return { label: 'Form skipped', tone: 'update' }
  if (row.eventType === 'form_unskipped') return { label: 'Form unskipped', tone: 'update' }
  if (row.eventType === 'signature_applied') return { label: 'Signed', tone: 'complete' }
  const empty = (v: unknown) => v === null || v === undefined || v === ''
  if (empty(row.oldValue) && !empty(row.newValue)) return { label: 'Entered', tone: 'create' }
  if (!empty(row.oldValue) && empty(row.newValue)) return { label: 'Cleared', tone: 'remove' }
  return { label: 'Changed', tone: 'update' }
}

export const FIELD_VIEW: AuditView = {
  tableId: 'audit-trail-field-v1',
  title: 'Form Field Changes',
  columns: [
    DATE,
    {
      id: 'form',
      header: 'Form',
      defaultWidth: 200,
      sortKey: 'form',
      cell: (row) =>
        text(fact(row, 'form') ?? (row.record?.kind === 'patient' ? 'Patient record' : row.record?.label ?? null), null, undefined, formLink(row)),
    },
    { id: 'subject', header: 'Patient / Subject', defaultWidth: 140, sortKey: 'subject', cell: subjectCell },
    { id: 'field', header: 'Field', defaultWidth: 200, sortKey: 'step', cell: (row) => text(fact(row, 'field') ?? row.itemChanged ?? null, null, true) },
    { id: 'change', header: 'Change', defaultWidth: 140, cell: (row) => ({ kind: 'badge', ...fieldChange(row) }) },
    {
      id: 'old',
      header: 'Previous Value',
      defaultWidth: 200,
      cell: (row) => ({ kind: 'lines', lines: changesOf(row).filter((c) => c.from !== undefined).map((c) => ({ label: changesOf(row).length > 1 ? c.label : undefined, value: c.from! })) }),
    },
    {
      id: 'new',
      header: 'New Value',
      defaultWidth: 200,
      // A new form version: the fields it removed, added and renamed.
      cell: (row) =>
        versionLines(row).length
          ? { kind: 'lines', lines: versionLines(row) }
          : { kind: 'lines', lines: changesOf(row).map((c) => ({ label: changesOf(row).length > 1 ? c.label : undefined, value: c.to })) },
    },
    BY,
    REASON,
  ],
}

// ─── User: everything people did — signed in, opened, created, changed ─────

const ACCESS = new Set(['login', 'logout', 'page_view'])

/** How long a session lasted, on its sign-out and on the sign-in that began it. */
export function sessionOf(row: AuditCenterRow): string | null {
  if (row.eventType !== 'login' && row.eventType !== 'logout') return null
  return fact(row, 'session') ?? (row.eventType === 'logout' ? (row.detail ?? '').replace(/^Session lasted /, '') || null : null)
}

/** When a session ended, or that no sign-out has been recorded for it. */
export function accessDetail(row: AuditCenterRow): string | null {
  if (row.eventType !== 'login') return null
  const out = fact(row, 'signedOutAt')
  return out ? `Signed out ${at(out)}` : 'No sign-out recorded yet (still signed in, or the browser was closed)'
}

const AREA_BY_RECORD: Partial<Record<string, string>> = {
  procedure: 'Procedures',
  submission: 'Forms',
  patient: 'Patients',
  product: 'Products',
  storage: 'Storage',
  zone: 'Storage',
  transfer: 'Storage',
  lot: 'Inventory',
  material: 'Inventory',
  pick_list: 'Inventory',
  profile: 'Inventory',
  equipment: 'Equipment',
  equipment_type: 'Equipment',
  form_definition: 'Form templates',
  procedure_template: 'Procedure templates',
  library_field: 'Field library',
  step_setup: 'Procedure templates',
  user: 'Users & roles',
  role: 'Users & roles',
  user_group: 'Users & roles',
  capability: 'Users & roles',
  instance: 'Settings',
  integration: 'Integrations',
  action_item: 'Action Items',
  mes_product: 'Product Codes',
}

/** Which part of the app an action was in. */
export function areaOf(row: AuditCenterRow): string {
  if (ACCESS.has(row.eventType)) return row.eventType === 'page_view' ? 'Navigation' : 'Session'
  // A process step, a stock move or a booking made from a form is procedure work.
  if (row.source === 'process') return row.record?.kind === 'procedure_template' ? 'Procedure templates' : 'Procedures'
  return (row.record && AREA_BY_RECORD[row.record.kind]) ?? titleCase(row.trail)
}

/** What the person did, as a sentence. */
export function activityOf(row: AuditCenterRow): string {
  if (row.eventType === 'login') return 'Signed in'
  if (row.eventType === 'logout') return 'Signed out'
  if (row.eventType === 'page_view') return `Opened ${pageName(row.detail ?? '')}`
  // Stock rows: the action and the stock it moved.
  if (row.action && row.item && (row.trail === 'storage' || row.trail === 'inventory' || row.source === 'inventory')) {
    return `${row.action} — ${row.quantity != null ? `${row.quantity} of ` : ''}${row.item}`
  }
  return row.description?.trim() || row.action || humanizeKey(row.eventType)
}

/** The details of what the person did: a session, a page's path, values from → to, where stock went, a new version's changes. */
export function activityDetail(row: AuditCenterRow): AuditCell {
  if (row.eventType === 'login') {
    return { kind: 'lines', lines: [sessionOf(row) ? { label: 'Session', value: sessionOf(row)! } : null, { value: accessDetail(row)! }].filter((l): l is { label?: string; value: string } => !!l) }
  }
  if (row.eventType === 'logout') return text(sessionOf(row) ? `Session lasted ${sessionOf(row)}` : null)
  if (row.eventType === 'page_view') return text(row.detail ?? null)
  const version = versionLines(row)
  if (version.length) return { kind: 'lines', lines: version }
  if (row.detailLines?.length) return { kind: 'changes', changes: row.detailLines }
  if (row.fromLocation || row.toLocation) {
    return {
      kind: 'lines',
      lines: [
        row.fromLocation ? { label: 'From', value: row.fromLocation } : null,
        row.toLocation ? { label: 'To', value: row.toLocation } : null,
      ].filter((l): l is { label: string; value: string } => !!l),
    }
  }
  const pairs = (row.detail ?? '').split('; ').map((p) => /^(.+?): (.*) → (.*)$/.exec(p))
  if (row.detail && pairs.every(Boolean)) {
    return { kind: 'changes', changes: pairs.map((m) => ({ label: humanizeKey(m![1]), from: m![2], to: m![3] })) }
  }
  // A process step reads as it does on the Procedures trail: its signature, and the equipment and lots it selected, in words.
  return { kind: 'changes', changes: row.source === 'process' ? procedureValues(row) : changesOf(row) }
}

export const USER_VIEW: AuditView = {
  tableId: 'audit-trail-user-v2',
  title: 'User Activity',
  columns: [
    DATE,
    // The one who did it — always. What it was done to is the Item.
    { id: 'user', header: 'User', defaultWidth: 230, sortKey: 'by', cell: () => ({ kind: 'actor' }) },
    { id: 'activity', header: 'Activity', defaultWidth: 340, cell: (row) => text(activityOf(row), null, true) },
    {
      id: 'item',
      header: 'Item',
      defaultWidth: 220,
      sortKey: 'record',
      // What it was done to; a sign-in or page visit is about no one item.
      cell: (row) => (ACCESS.has(row.eventType) ? text(null) : { kind: 'record' }),
    },
    { id: 'details', header: 'Details', defaultWidth: 280, cell: activityDetail },
    { id: 'area', header: 'Area', defaultWidth: 150, cell: (row) => text(areaOf(row)) },
    REASON,
  ],
}

// ─── Patient: everything that happened for one patient ──────────────────────

/**
 * What happened, in the words of the trail the row comes from: a product row
 * as Product says it, a patient record or visit as Field says it, and form,
 * step, stock and booking rows as Procedures says them.
 */
export function patientActivity(row: AuditCenterRow): { label: string; tone: ChangeTone } {
  switch (fact(row, 'kind')) {
    case 'product':
      return productActivity(row)
    case 'patient':
      return row.itemChanged === 'Patient visit'
        ? { label: `Visit ${humanizeKey(row.eventType).toLowerCase()}`, tone: 'update' }
        : fieldChange(row)
    default:
      return procedureActivity(row)
  }
}

/** The form, product, unit, lot or record a Patient row is about, opening its own trail. */
export function patientItem(row: AuditCenterRow): AuditCell {
  switch (fact(row, 'kind')) {
    case 'product':
      return row.productId
        ? text(row.din ?? row.record?.label ?? null, row.productCode ?? null, true, { source: productSource(row) })
        : { kind: 'record' }
    case 'equipment':
      return text(fact(row, 'equipment'), stepAndForm(row), true, { source: relatedSource(row, 'equipment') })
    case 'inventory':
      return text(
        fact(row, 'lotNumber') ? `Lot ${fact(row, 'lotNumber')}` : null,
        [fact(row, 'materialName'), stepAndForm(row)].filter(Boolean).join(' · ') || null,
        true,
        { source: relatedSource(row, 'lot') },
      )
    case 'form':
    case 'procedure':
      return fact(row, 'form') || row.record?.kind === 'submission'
        ? text(fact(row, 'form') ?? row.record?.label ?? null, fact(row, 'step') ?? fact(row, 'field') ?? row.itemChanged, true, formLink(row))
        : { kind: 'record' }
    default:
      return text(row.itemChanged === 'Patient visit' ? 'Patient visit' : 'Patient record', row.itemChanged === 'Patient visit' ? null : row.itemChanged, true, formLink(row))
  }
}

export const PATIENT_VIEW: AuditView = {
  tableId: 'audit-trail-patient-v1',
  title: 'Patient History',
  columns: [
    DATE,
    {
      id: 'kind',
      header: 'Kind',
      defaultWidth: 110,
      sortKey: 'kind',
      cell: (row) => ({ kind: 'badge', label: patientKindLabel(fact(row, 'kind')) ?? '—', tone: 'access' }),
    },
    { id: 'patient', header: 'Patient', defaultWidth: 140, sortKey: 'subject', cell: subjectCell },
    { id: 'item', header: 'Form / Item', defaultWidth: 220, cell: patientItem },
    { id: 'activity', header: 'Activity', defaultWidth: 150, cell: (row) => ({ kind: 'badge', ...patientActivity(row) }) },
    {
      id: 'change',
      header: 'Change',
      defaultWidth: 240,
      cell: (row) => ({ kind: 'changes', changes: procedureValues(row) }),
    },
    BY,
    { id: 'description', header: 'Description', defaultWidth: 220, sortKey: 'description', defaultHidden: true, cell: (row) => text(row.description ?? null) },
    REASON,
  ],
}

// ─── Labels: every label printed, reprints included ─────────────────────────

const PRINT_KIND: Record<string, { label: string; tone: ChangeTone }> = {
  original: { label: 'First print', tone: 'create' },
  reprint: { label: 'Reprint', tone: 'update' },
  relabel: { label: 'Replacement', tone: 'stock' },
}

export function labelPrintKind(row: AuditCenterRow): { label: string; tone: ChangeTone } {
  const kind = fact(row, 'printKind')
  return (kind ? PRINT_KIND[kind] : undefined) ?? { label: kind ? humanizeKey(kind) : '—', tone: 'update' }
}

/**
 * The label a reprint or replacement replaces, opening the Labels trail at it.
 * A label has no page of its own; its number finds it in this trail.
 */
export function replacedLabel(row: AuditCenterRow): AuditCell {
  const number = fact(row, 'replacesNumber')
  if (!number) return text(null)
  const to = `/audit?${new URLSearchParams({ sources: 'labels', q: `#${number}` }).toString()}`
  return text(`Label #${number}`, null, undefined, { target: { to, permissions: [PERMISSIONS.auditRead, PERMISSIONS.auditLabelsRead] } })
}

export function labelUsage(row: AuditCenterRow): { label: string; tone: ChangeTone } {
  const usage = fact(row, 'usage') as LabelUsage | null
  if (!usage) return { label: '—', tone: 'update' }
  const tone: Record<LabelUsage, ChangeTone> = { pending: 'stock', applied: 'complete', discarded: 'remove', unused: 'access' }
  return { label: USAGE_LABEL[usage] ?? humanizeKey(usage), tone: tone[usage] ?? 'update' }
}

export const LABELS_VIEW: AuditView = {
  tableId: 'audit-trail-labels-v1',
  title: 'Label Prints',
  columns: [
    { ...DATE, header: 'Printed At' },
    { id: 'number', header: 'Label #', defaultWidth: 90, cell: (row) => text(fact(row, 'labelNumber') ? `#${fact(row, 'labelNumber')}` : null, null, true) },
    {
      id: 'din',
      header: 'DIN',
      defaultWidth: 160,
      sortKey: 'din',
      cell: (row) => text(row.din ?? null, null, true, { source: productSource(row) }),
    },
    {
      id: 'code',
      header: 'Product Code',
      defaultWidth: 120,
      sortKey: 'productCode',
      cell: (row) => text(row.productCode ?? null, null, undefined, { source: productSource(row) }),
    },
    { id: 'labelType', header: 'Label Type', defaultWidth: 140, cell: (row) => text(fact(row, 'labelType')) },
    { id: 'printKind', header: 'Print Kind', defaultWidth: 130, cell: (row) => ({ kind: 'badge', ...labelPrintKind(row) }) },
    { id: 'replaces', header: 'Replaces', defaultWidth: 110, cell: replacedLabel },
    { id: 'usage', header: 'Usage', defaultWidth: 140, cell: (row) => ({ kind: 'badge', ...labelUsage(row) }) },
    { id: 'patient', header: 'Patient', defaultWidth: 140, sortKey: 'subject', cell: subjectCell },
    { ...BY, header: 'Printed By' },
    { id: 'meaning', header: 'Signature Meaning', defaultWidth: 240, cell: (row) => text(fact(row, 'signatureMeaning')) },
    // Why a label was discarded.
    REASON,
  ],
}

// ─── Offline sync: every action a phone did offline, applied or not ─────────

const OFFLINE_STATUS: Record<string, { label: string; tone: ChangeTone }> = {
  APPLIED: { label: 'Applied', tone: 'complete' },
  DUPLICATE: { label: 'Duplicate', tone: 'access' },
  CONFLICT: { label: 'Conflict', tone: 'stock' },
  REJECTED: { label: 'Rejected', tone: 'remove' },
}

/** What the server did with an offline action. */
export function offlineStatus(row: AuditCenterRow): { label: string; tone: ChangeTone } {
  const status = fact(row, 'status')
  return (status ? OFFLINE_STATUS[status] : undefined) ?? { label: status ? humanizeKey(status) : '—', tone: 'update' }
}

/** When it reached the server, and how long after it was done. */
export function offlineSynced(row: AuditCenterRow): AuditCell {
  const seconds = Number(fact(row, 'delaySeconds') ?? NaN)
  const delay = Number.isFinite(seconds)
    ? seconds < 60
      ? 'right away'
      : seconds < 3600
        ? `${Math.round(seconds / 60)} min later`
        : `${Math.round((seconds / 3600) * 10) / 10} h later`
    : null
  return text(at(fact(row, 'syncedAt')), delay)
}

export const OFFLINE_VIEW: AuditView = {
  tableId: 'audit-trail-offline-v1',
  title: 'Offline Sync',
  columns: [
    { ...DATE, header: 'Done At' },
    { id: 'synced', header: 'Synced At', defaultWidth: 150, cell: offlineSynced },
    { id: 'equipment', header: 'Equipment / Room', defaultWidth: 150, sortKey: 'record', cell: () => ({ kind: 'record' }) },
    { id: 'action', header: 'Action', defaultWidth: 160, cell: (row) => text(row.action ?? null, fact(row, 'task')) },
    { id: 'status', header: 'Status', defaultWidth: 110, cell: (row) => ({ kind: 'badge', ...offlineStatus(row) }) },
    // The server's reason, in its own words, for anything not applied.
    { id: 'reason', header: 'Reason', defaultWidth: 260, sortKey: 'reason', cell: (row) => text(row.reason?.trim() || null) },
    BY,
    { id: 'device', header: 'Device', defaultWidth: 100, cell: (row) => text(fact(row, 'device')) },
    {
      id: 'acknowledged',
      header: 'Acknowledged',
      defaultWidth: 150,
      defaultHidden: true,
      cell: (row) => text(at(fact(row, 'acknowledgedAt'))),
    },
  ],
}

export const AUDIT_VIEWS: Record<AuditCenterSource, AuditView> = {
  storage: STORAGE_VIEW,
  inventory: INVENTORY_VIEW,
  product: PRODUCT_VIEW,
  equipment: EQUIPMENT_VIEW,
  procedure: PROCEDURE_VIEW,
  field: FIELD_VIEW,
  user: USER_VIEW,
  patient: PATIENT_VIEW,
  labels: LABELS_VIEW,
  offline: OFFLINE_VIEW,
}
